import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Vote, Mail, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LanguageSelector } from "../components/LanguageSelector";
import { toast } from "sonner";
import api from "../lib/api";

import { useAppConfig } from "../lib/AppConfigContext";

export default function ForgotPasswordPage() {
  const { appName } = useAppConfig();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post("/entities/request-password-reset", { email });
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      {/* Language selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
            <Vote className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-xl text-slate-900">{appName}</h1>
            <p className="text-sm text-slate-500">{t('consulta.title')}</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
          {!sent ? (
            <>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                {t('auth.resetPassword')}
              </h2>
              <p className="text-slate-500 mb-6">
                {t('auth.resetPasswordSubtitle')}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="form-group">
                  <Label htmlFor="email" className="form-label">
                    {t('auth.email')}
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11"
                      placeholder={t('forgotPassword.emailPlaceholder')}
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800"
                  disabled={loading}
                  data-testid="forgot-submit"
                >
                  {loading ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      {t('auth.sendResetLink')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                {t('auth.resetEmailSent')}
              </h2>
              <p className="text-slate-500 mb-6">
                {t('auth.resetEmailSentMessage')}
              </p>
              <p className="text-sm text-slate-400">
                {t('auth.checkSpam')}
              </p>
            </div>
          )}
        </div>

        <div className="text-center mt-6">
          <Link
            to="/login"
            className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('auth.backToLogin')}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
